import { useLayoutEffect, useState } from "react";
import {
  BAR_GRADIENT_HOVER_LIGHTNESS_LIFT,
  BAR_GRADIENT_LIGHTNESS_LIFT,
  BAR_GRADIENT_MID_OFFSET,
  barGradientHoverId,
  barGradientId,
  buildBarGradientStops,
  type BarGradientStops,
} from "./chartBarGradients.js";

interface BarSeriesGradientDefsProps {
  chartInstanceId: string;
  metrics: ReadonlyArray<{ id: string; colorVar: string }>;
}

/**
 * One vertical linearGradient per series. Legend/tooltip keep solid base colors;
 * only bar fills reference these defs.
 */
export function BarSeriesGradientDefs({ chartInstanceId, metrics }: BarSeriesGradientDefsProps) {
  const [stopsBySeries, setStopsBySeries] = useState<
    Record<string, { default: BarGradientStops; hover: BarGradientStops }>
  >({});

  useLayoutEffect(() => {
    const root = document.documentElement;
    const next: Record<string, { default: BarGradientStops; hover: BarGradientStops }> = {};
    for (const metric of metrics) {
      next[metric.id] = {
        default: buildBarGradientStops(metric.colorVar, root, BAR_GRADIENT_LIGHTNESS_LIFT),
        hover: buildBarGradientStops(metric.colorVar, root, BAR_GRADIENT_HOVER_LIGHTNESS_LIFT),
      };
    }
    setStopsBySeries(next);
  }, [metrics]);

  return (
    <defs>
      {metrics.map((metric) => {
        const stops = stopsBySeries[metric.id];
        if (!stops) return null;
        const gradientId = barGradientId(chartInstanceId, metric.id);
        const hoverGradientId = barGradientHoverId(chartInstanceId, metric.id);
        return (
          <g key={metric.id}>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stops.default.top} />
              <stop offset={BAR_GRADIENT_MID_OFFSET} stopColor={stops.default.mid} />
              <stop offset="100%" stopColor={stops.default.base} />
            </linearGradient>
            <linearGradient id={hoverGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stops.hover.top} />
              <stop offset={BAR_GRADIENT_MID_OFFSET} stopColor={stops.hover.mid} />
              <stop offset="100%" stopColor={stops.hover.base} />
            </linearGradient>
          </g>
        );
      })}
    </defs>
  );
}
